import { verificationAdminService } from "@/api/admin/verification-admin.service";
import type { TPhoneLogRecord } from "@/api/admin/verification-admin.types";
import {
  DataGrid,
  type TColumnDef,
  type TDataGridQuery,
  type TDateRangeValue,
  type TFilterDef,
} from "@/components/data-grid";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/utils";
import { Lock, LockOpen } from "lucide-react";
import { useState } from "react";

const PAGE_SIZE = 20;

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value ? value : undefined;
}

function Detail({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | null | undefined;
  mono?: boolean;
}) {
  return (
    <div>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <p className={`mt-0.5 break-all text-sm ${mono ? "font-mono text-xs" : ""}`}>{value ?? "-"}</p>
    </div>
  );
}

export function VerificationPhoneLogsPage() {
  const { toast } = useToast();
  const [reloadTick, setReloadTick] = useState(0);

  const fetcher = async (query: TDataGridQuery) => {
    const dateRange = (query.filters.created as TDateRangeValue | undefined) ?? {};
    const response = await verificationAdminService.listPhoneLogs(
      {
        limit: query.limit,
        offset: query.offset,
        ...(query.search ? { search: query.search } : {}),
        ...(query.sort_by ? { sort_by: query.sort_by, sort_order: query.sort_order } : {}),
        ...(asString(query.filters.user_id) ? { user_id: asString(query.filters.user_id) } : {}),
        ...(asString(query.filters.phone_number)
          ? { phone_number: asString(query.filters.phone_number) }
          : {}),
        ...(asString(query.filters.delivery_status)
          ? { delivery_status: asString(query.filters.delivery_status) }
          : {}),
        ...(dateRange.from ? { date_from: dateRange.from } : {}),
        ...(dateRange.to ? { date_to: dateRange.to } : {}),
      },
      { signal: query.signal }
    );
    if (response.error) throw response.error;
    return response.data;
  };

  const handleLegalHoldToggle = async (row: TPhoneLogRecord) => {
    const { error: err } = row.legal_hold
      ? await verificationAdminService.releaseLegalHold(row.id)
      : await verificationAdminService.setLegalHold(row.id);
    if (err) {
      toast({ variant: "destructive", title: await extractErrorMessage(err) });
    } else {
      toast({ title: row.legal_hold ? "Legal hold released" : "Legal hold applied" });
      setReloadTick((t) => t + 1);
    }
  };

  // Backend-whitelisted sort columns for verification/phone-logs: created_at, delivery_status.
  const columns: TColumnDef<TPhoneLogRecord>[] = [
    {
      id: "phone_number",
      header: "Phone",
      accessor: (r) => r.phone_number ?? "",
      cell: (r) => (
        <span className="inline-flex items-center gap-1 text-sm font-medium">
          <span className="truncate">{r.phone_number}</span>
          {r.legal_hold && (
            <span className="inline-flex items-center gap-0.5 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
              <Lock className="h-2.5 w-2.5" />
              Hold
            </span>
          )}
        </span>
      ),
    },
    {
      id: "delivery_status",
      header: "Status",
      type: "badge",
      sortable: true,
      sortKey: "delivery_status",
      accessor: (r) => r.delivery_status,
      meta: {
        badgeVariant: (v) =>
          v === "delivered"
            ? "default"
            : v === "failed"
              ? "destructive"
              : v === "accepted"
                ? "outline"
                : "secondary",
      },
    },
    { id: "attempts", header: "Tries", accessor: (r) => String(r.attempts) },
    {
      id: "last_delivery_error",
      header: "Last Error",
      accessor: (r) => r.last_delivery_error ?? "",
      cell: (r) =>
        r.last_delivery_error ? (
          <span className="block max-w-xs truncate text-xs text-destructive" title={r.last_delivery_error}>
            {r.last_delivery_error}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        ),
    },
    { id: "user_id", header: "User ID", type: "uuid", defaultHidden: true, accessor: (r) => r.user_id },
    { id: "created_at", header: "Created", type: "date", sortable: true, sortKey: "created_at" },
  ];

  const filters: TFilterDef[] = [
    { id: "user_id", type: "user", label: "User", placeholder: "Search by name or email…" },
    { id: "phone_number", type: "text", label: "Phone", placeholder: "Phone (+254...)" },
    {
      id: "delivery_status",
      type: "select",
      label: "Status",
      options: [
        { label: "Queued", value: "queued" },
        { label: "Accepted", value: "accepted" },
        { label: "Delivered", value: "delivered" },
        { label: "Failed", value: "failed" },
      ],
    },
    { id: "created", type: "dateRange", label: "Date" },
  ];

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground sm:text-3xl">WhatsApp Delivery Logs</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Inspect OTP WhatsApp delivery records and manage legal holds
          </p>
        </div>

        <DataGrid<TPhoneLogRecord>
          getRowId={(row) => row.id}
          persistKey="admin.verification.phone"
          ariaLabel="WhatsApp delivery logs"
          data={{ mode: "server", fetcher, deps: [reloadTick] }}
          columns={columns}
          filters={filters}
          pagination={{ mode: "offset", pageSize: PAGE_SIZE, pageSizeOptions: [10, 20, 50, 100] }}
          toolbar={{
            search: true,
            refresh: true,
            columnVisibility: true,
            density: true,
            export: ["csv", "json"],
            searchPlaceholder: "Search phone",
          }}
          details={{
            variant: "drawer",
            title: (row) => row.phone_number,
            render: (row) => (
              <div className="space-y-4 p-4">
                <Badge
                  variant={
                    row.delivery_status === "delivered"
                      ? "default"
                      : row.delivery_status === "failed"
                        ? "destructive"
                        : "secondary"
                  }
                >
                  {row.delivery_status}
                </Badge>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Detail label="Record ID" value={row.id} mono />
                  <Detail label="User ID" value={row.user_id} mono />
                  <Detail label="Verification Request ID" value={row.verification_request_id} mono />
                  <Detail label="WhatsApp Message ID" value={row.whatsapp_message_id} mono />
                  <Detail label="Attempts" value={String(row.attempts)} />
                  <Detail label="Created At" value={fmtDate(row.created_at)} />
                  <Detail label="Expires At" value={fmtDate(row.expires_at)} />
                  <Detail label="Verified At" value={fmtDate(row.verified_at)} />
                  <Detail label="Delivered At" value={fmtDate(row.delivered_at)} />
                </div>
                {row.last_delivery_error && (
                  <div>
                    <p className="mb-1 text-xs font-medium text-muted-foreground">Raw Delivery Error</p>
                    <pre className="max-h-40 overflow-auto rounded bg-destructive/10 p-2 text-xs text-destructive">
                      {row.last_delivery_error}
                    </pre>
                  </div>
                )}
              </div>
            ),
          }}
          rowActions={(row) => [
            {
              label: row.legal_hold ? "Release legal hold" : "Set legal hold",
              icon: row.legal_hold ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />,
              onClick: () => void handleLegalHoldToggle(row),
            },
          ]}
          emptyState={{ title: "No WhatsApp delivery logs found." }}
        />
      </div>
    </div>
  );
}
